import type { SupplyAgentSimulateResponse } from '@/services/supplyAgentService';

export interface AgentTableRowOverlay {
  productId: number;
  sku: string;
  adjustedQty: number;
  systemQty: number;
  delta: number;
}

export interface AgentTableOverlay {
  supplierId: number;
  warehouseId: number;
  appliedRulesCount: number;
  hasChanges: boolean;
  byProductId: Record<number, AgentTableRowOverlay>;
}

type SimulationLine = {
  product_id: number;
  sku?: string;
  suggested_quantity?: number;
};

export function buildAgentTableOverlay(
  simulation: SupplyAgentSimulateResponse | null,
  supplierId: number,
  warehouseId: number,
): AgentTableOverlay | null {
  if (!simulation || simulation.status !== 'ok') return null;

  const beforeLines = (
    (simulation.before as { lines_detail?: SimulationLine[] } | undefined)?.lines_detail ?? []
  ) as SimulationLine[];
  const afterLines = (simulation.lines_detail ?? []) as SimulationLine[];

  const afterMap = new Map<number, SimulationLine>();
  for (const line of afterLines) {
    afterMap.set(line.product_id, line);
  }

  const byProductId: Record<number, AgentTableRowOverlay> = {};
  let hasChanges = false;

  const productIds = new Set<number>([
    ...beforeLines.map((line) => line.product_id),
    ...afterLines.map((line) => line.product_id),
  ]);

  for (const productId of productIds) {
    const beforeLine = beforeLines.find((line) => line.product_id === productId);
    const afterLine = afterMap.get(productId);
    const systemQty = Math.max(0, Number(beforeLine?.suggested_quantity ?? afterLine?.suggested_quantity ?? 0));
    const adjustedQty = Math.max(0, Number(afterLine?.suggested_quantity ?? 0));
    if (systemQty <= 0 && adjustedQty <= 0) continue;

    const delta = adjustedQty - systemQty;
    // Solo líneas con cambio: si no, el overlay pisaría el sugerido de tabla (MP/MOQ).
    if (delta === 0) continue;
    hasChanges = true;

    byProductId[productId] = {
      productId,
      sku: afterLine?.sku ?? beforeLine?.sku ?? '',
      adjustedQty,
      systemQty,
      delta,
    };
  }

  if (Object.keys(byProductId).length === 0) return null;

  return {
    supplierId,
    warehouseId,
    appliedRulesCount: simulation.applied_rules?.length ?? 0,
    hasChanges,
    byProductId,
  };
}

/** Solo devolver overlay si la fila pertenece al proveedor simulado. */
export function getAgentOverlayForProduct(
  overlay: AgentTableOverlay | null | undefined,
  productId: number,
  supplierId: number | null | undefined,
): AgentTableRowOverlay | null {
  if (!overlay || supplierId == null) return null;
  if (supplierId !== overlay.supplierId) return null;
  return overlay.byProductId[productId] ?? null;
}
